import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Card, CardBody, Input } from '@img/ui';
import { api, type ApiSuccess } from '../lib/api';
import { useSession } from '../lib/auth-client';
import { connectAuctionSocket, type AttendeeUser, type AuctionSocketHandle } from '../lib/auction-socket';
import { playStageSound, unlockAudio } from '../lib/sounds';
import { useToasts } from '../components/Toast';
import { SEO } from '../components/SEO';

interface Auction {
  id: string;
  title: string;
  city: string | null;
  description: string | null;
  coverImage: string | null;
  images: string[] | null;
  openingPrice: number | string;
  currentBid: number | string;
  bidIncrement: number | string;
  commissionRate: number | string;        // % السعي
  startAt: string;
  endAt: string;
  status: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED' | 'COMPLETED';
  totalBids: number;
  cancellationReason?: string | null;
  cancelledAt?: string | null;
}

interface BidEntry {
  id: string; userId: string; name: string; amount: number; at: Date;
}
interface ChatEntry {
  id: string; userId: string | null; name: string; message: string; at: Date; isMine?: boolean;
}
interface SpeakRequest { userId: string; name: string; at: number; }

const STAGE_COOLDOWN_SECONDS = 5;
const VAT_TAX_RATE = 0.05; // ضريبة التصرفات العقارية = 5%

export function AuctionRoomPage() {
  const { id } = useParams();
  const { data: session } = useSession();
  const user = session?.user as ({ id: string; name: string; role: string } & { customPermissions?: string[] }) | undefined;
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const perms = user?.customPermissions ?? [];
  const canAuctioneer = isAdmin || perms.includes('auctioneer');
  const canVoice      = isAdmin || perms.includes('auction.voice') || perms.includes('auctioneer');
  const canGrantMic   = isAdmin || perms.includes('auction.grant_mic') || perms.includes('auctioneer');

  const { data: auctionRes } = useQuery({
    queryKey: ['auction', id],
    queryFn: () => api.get<ApiSuccess<Auction>>(`/api/auctions/${id}`),
    enabled: !!id,
  });

  const [bids, setBids] = useState<BidEntry[]>([]);
  const [chat, setChat] = useState<ChatEntry[]>([]);
  const [attendees, setAttendees] = useState<AttendeeUser[]>([]);
  const [stageBanner, setStageBanner] = useState<{ stage: number; label: string; color: string } | null>(null);
  const [currentBid, setCurrentBid] = useState<number>(0);
  const [endAt, setEndAt] = useState<Date | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [bidInput, setBidInput] = useState<string>('');
  const [tab, setTab] = useState<'bids' | 'chat' | 'attendees'>('bids');
  const [imgIndex, setImgIndex] = useState(0);
  const [auctionEnded, setAuctionEnded] = useState<{
    winner: { id: string; name: string } | null;
    finalAmount: number;
    noSale?: boolean;
    noSaleReason?: string | null;
  } | null>(null);

  // Mic state
  const [grantedMics, setGrantedMics] = useState<Set<string>>(new Set());
  const [pendingRequests, setPendingRequests] = useState<SpeakRequest[]>([]);
  const [myMicOn, setMyMicOn] = useState(false);

  // 🛡️ تحكّم الجمهور
  const [mutedUsers, setMutedUsers] = useState<Set<string>>(new Set());
  const [bannedUsers, setBannedUsers] = useState<Set<string>>(new Set());

  // 🍞 Toasts
  const { show: showToast, ToastContainer } = useToasts();

  // 💬 مودال تأكيد البيع (بديل confirm() المتصفح)
  const [showSellConfirm, setShowSellConfirm] = useState(false);
  const [showNotSold, setShowNotSold] = useState(false);
  const [notSoldReason, setNotSoldReason] = useState('');

  // 💰 شريط المزايدة العائم (fixed bottom) — قابل للتصغير
  const [bidBarMinimized, setBidBarMinimized] = useState(false);

  // Stage state
  const [stageProgress, setStageProgress] = useState(0);
  const [cooldownEnd, setCooldownEnd] = useState(0);
  const [, forceTick] = useState(0);

  const socketRef = useRef<AuctionSocketHandle | null>(null);

  useEffect(() => {
    const t = setInterval(() => forceTick((x) => x + 1), 250);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!id) return;
    unlockAudio();

    const auction = auctionRes?.data;
    if (auction) {
      setCurrentBid(Number(auction.currentBid) || Number(auction.openingPrice));
      setEndAt(new Date(auction.endAt));
      if (auction.status === 'COMPLETED' || auction.status === 'ENDED') {
        // 🏆 لو في فائز معروف من السيرفر، اعرض رسالة "الله يبارك"
        const w = (auction as { winner?: { id: string; name: string } | null }).winner ?? null;
        const cancellationReason = (auction as { cancellationReason?: string | null }).cancellationReason ?? null;
        setAuctionEnded({
          winner: w,
          finalAmount: Number(auction.currentBid),
          // إذا في cancellationReason ولا في فائز — يعتبر "لم يتم البيع"
          noSale: !w && !!cancellationReason,
          noSaleReason: cancellationReason,
        });
      }
    }

    const handle = connectAuctionSocket(id, {
      userName: user?.name,
      onAttendees: (list) => setAttendees(list),
      onUserJoined: ({ user: u, totalAttendees }) => {
        setAttendees((cur) => [...cur, u]);
        setChat((c) => [...c, { id: `sys-${Date.now()}`, userId: null, name: 'النظام', message: `${u.name} دخل المزاد (${totalAttendees})`, at: new Date() }]);
      },
      onUserLeft: ({ user: u }) => {
        setAttendees((cur) => cur.filter((a) => a.userId !== u.userId));
        setGrantedMics((cur) => { const n = new Set(cur); if (u.userId) n.delete(u.userId); return n; });
      },
      onBidsHistory: (h) => {
        setBids(h.map((b) => ({ id: b.id, userId: b.userId, name: b.user?.name ?? 'مزايد', amount: Number(b.amount), at: new Date(b.createdAt) })));
        if (h[0]) setCurrentBid(Number(h[0].amount));
      },
      onMessagesHistory: (h) => {
        setChat(h.map((m) => ({ id: m.id, userId: m.userId, name: m.user?.name ?? 'النظام', message: m.message, at: new Date(m.createdAt) })));
      },
      onNewBid: (b) => {
        setBids((c) => [{ id: b.id, userId: b.userId, name: b.userName, amount: b.amount, at: new Date(b.createdAt) }, ...c]);
        setCurrentBid(b.amount);
        setStageProgress(0);
        setCooldownEnd(0);
        setStageBanner(null);
        playStageSound('bid');
      },
      onNewMessage: (m) => {
        const isMine = user && m.userId === user.id;
        setChat((c) => [...c, { id: m.id, userId: m.userId, name: m.userName, message: m.message, at: new Date(m.createdAt), isMine }]);
      },
      onStageChanged: (d) => {
        if (d.reason === 'new_bid' || d.stage === 0) {
          setStageBanner(null); setStageProgress(0); setCooldownEnd(0);
          return;
        }
        const labels: Record<number, { label: string; color: string }> = {
          1: { label: '🔔 حراج (مرة أولى)',                  color: '#fbbf24' },
          2: { label: '🔔🔔 حراج (مرة ثانية)',                 color: '#f59e0b' },
          3: { label: '🔔🔔🔔 حراج (مرة ثالثة) — آخر فرصة!', color: '#dc2626' },
          4: { label: '✅ تم البيع — الله يبارك للبائع والمشتري', color: '#16a34a' },
        };
        const cfg = labels[d.stage];
        if (cfg) {
          setStageBanner({ stage: d.stage, ...cfg });
          setStageProgress(d.stage);
          if (d.at && Date.now() - d.at < 10_000) playStageSound(d.stage as 1 | 2 | 3 | 4);
        }
      },
      onAuctionExtended: ({ newEndAt }) => setEndAt(new Date(newEndAt)),
      onOutbid: ({ newAmount }) => showToast({
        kind: 'warn',
        title: 'تفوّق عليك مزايد آخر',
        body: `المزايدة الجديدة ${newAmount.toLocaleString('en-US')} ر.س — زايد للحفاظ على مكانتك!`,
      }),
      onJoinDenied: ({ message }) => showToast({
        kind: 'error',
        title: 'لا يمكن الدخول للمزاد',
        body: message,
        duration: 8000,
      }),
      onSpeakState: ({ granted, pending }) => {
        setGrantedMics(new Set(granted));
        setPendingRequests(pending);
      },
      onSpeakRequest:           (d) => setPendingRequests((c) => c.find((r) => r.userId === d.userId) ? c : [...c, d]),
      onSpeakRequestCancelled:  (d) => setPendingRequests((c) => c.filter((r) => r.userId !== d.userId)),
      onSpeakRequestDenied:     (d) => setPendingRequests((c) => c.filter((r) => r.userId !== d.userId)),
      onMicGranted:             (d) => setGrantedMics((c) => new Set([...c, d.userId])),
      onMicRevoked:             (d) => setGrantedMics((c) => { const n = new Set(c); n.delete(d.userId); return n; }),
      onMicState:               (d) => {
        setGrantedMics((c) => { const n = new Set(c); if (d.on) n.add(d.userId); else n.delete(d.userId); return n; });
        if (user && d.userId === user.id) setMyMicOn(d.on);
      },
      onAuctionEnded: (d) => setAuctionEnded(d),
      // 🛡️ تحكّم الجمهور
      onUserMuted: ({ userId, by }) => {
        setMutedUsers((c) => new Set([...c, userId]));
        showToast({ kind: 'warn', title: '🔇 تم كتم مستخدم من الشات', body: `بواسطة: ${by}` });
      },
      onUserUnmuted: ({ userId }) => {
        setMutedUsers((c) => { const n = new Set(c); n.delete(userId); return n; });
      },
      onUserKicked: ({ userId, by }) => {
        showToast({ kind: 'warn', title: '🚫 تم طرد مستخدم', body: `بواسطة: ${by}` });
        // إزالة من قائمة الحضور (سيُحدث الـ server attendees لاحقاً)
        void userId;
      },
      onUserBanned: ({ userId, by }) => {
        setBannedUsers((c) => new Set([...c, userId]));
        showToast({ kind: 'error', title: '⛔ تم حظر مستخدم', body: `بواسطة: ${by}` });
      },
      onKicked: ({ by, reason }) => {
        showToast({
          kind: 'error',
          title: reason ?? 'تم إخراجك من الغرفة',
          body: `بواسطة: ${by}`,
          duration: 10000,
        });
        // العودة لصفحة المزادات بعد 3 ثوانٍ
        setTimeout(() => { window.location.href = '/auctions'; }, 3000);
      },
    });
    socketRef.current = handle;

    return () => { handle.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, auctionRes?.data?.id]);

  // 🎯 الصور — useMemo لازم يكون قبل أي return عشان قاعدة الـ hooks
  const images = useMemo(() => {
    const a = auctionRes?.data;
    const list: string[] = [];
    if (a?.coverImage) list.push(a.coverImage);
    if (Array.isArray(a?.images)) list.push(...a.images.filter((x) => !list.includes(x)));
    return list;
  }, [auctionRes?.data?.coverImage, auctionRes?.data?.images]);

  if (!auctionRes?.data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0F1114] text-gray-400">⟳ جاري التحميل…</div>;
  }
  const auction = auctionRes.data;

  // ⏱ countdown
  const remainingSec = endAt ? Math.max(0, Math.floor((+endAt - Date.now()) / 1000)) : 0;
  const dd = Math.floor(remainingSec / 86400);
  const hh = Math.floor((remainingSec % 86400) / 3600);
  const mm = Math.floor((remainingSec % 3600) / 60);
  const ss = remainingSec % 60;

  const minBid = currentBid + Number(auction.bidIncrement);

  // 💰 تفصيل السعر
  const commissionRate = Number(auction.commissionRate) / 100;            // مثلاً 2.5% = 0.025
  const commission     = currentBid * commissionRate;                      // السعي
  const tax            = currentBid * VAT_TAX_RATE;                        // ضريبة التصرفات
  const totalDue       = currentBid + commission + tax;                    // الإجمالي

  // 🎯 الزر المتسلسل الخطّي: 1 → 2 → 3 → بيع الله يبارك له → (انتهى)
  // مايقدر المحرّج يتجاوز أو يقفز — بس يمشي خطوة خطوة
  const cooldownLeft = Math.max(0, Math.ceil((cooldownEnd - Date.now()) / 1000));
  const isCooling = cooldownLeft > 0;
  const stageDone = stageProgress >= 4;
  const nextStage = stageDone ? 4 : (stageProgress + 1) as 1 | 2 | 3 | 4;
  const stageLabel = isCooling ? `⏳ ${cooldownLeft}s`
    : stageDone        ? '✓ تم البيع'
    : nextStage === 1  ? 'حراج 1'
    : nextStage === 2  ? 'حراج 2'
    : nextStage === 3  ? 'حراج 3'
    : '✓ تم البيع — الله يبارك للبائع والمشتري';
  const stageBg = isCooling ? '#374151'
    : stageDone        ? '#15803d'
    : nextStage === 1  ? 'linear-gradient(135deg,#fbbf24,#f59e0b)'
    : nextStage === 2  ? 'linear-gradient(135deg,#f59e0b,#ea580c)'
    : nextStage === 3  ? 'linear-gradient(135deg,#ea580c,#dc2626)'
    : 'linear-gradient(135deg,#16a34a,#15803d)';

  const handleStageNext = () => {
    if (!socketRef.current || isCooling || stageDone) return;
    // 🚦 خطوة واحدة فقط — لا تجاوز
    // عند الوصول للمرحلة 4 (البيع)، نفتح مودال تأكيد بدل confirm() المتصفح
    if (nextStage === 4) {
      setShowSellConfirm(true);
      return;
    }
    setStageProgress(nextStage);
    socketRef.current.emitStage(nextStage);
    setCooldownEnd(Date.now() + STAGE_COOLDOWN_SECONDS * 1000);
  };

  const confirmSell = () => {
    if (!socketRef.current) return;
    setStageProgress(4);
    socketRef.current.emitStage(4);
    setCooldownEnd(Date.now() + STAGE_COOLDOWN_SECONDS * 1000);
    setShowSellConfirm(false);
  };
  const handleStageRepeat = () => {
    if (!socketRef.current || isCooling || stageProgress < 1 || stageProgress >= 4) return;
    socketRef.current.emitStage(stageProgress);
    setCooldownEnd(Date.now() + STAGE_COOLDOWN_SECONDS * 1000);
  };

  const handleSendChat = async () => {
    if (!chatInput.trim() || !socketRef.current) return;
    if (!user) {
      showToast({ kind: 'info', title: 'سجّل دخولك للمشاركة في الشات' });
      return;
    }
    try {
      await socketRef.current.sendChat(chatInput.trim());
      setChatInput('');
    } catch (e) {
      showToast({ kind: 'error', title: 'تعذّر إرسال الرسالة', body: (e as Error).message });
    }
  };
  const handlePlaceBid = async () => {
    if (!socketRef.current) return;
    if (!user) {
      showToast({ kind: 'info', title: 'سجّل دخولك للمزايدة' });
      return;
    }
    const amount = Number(bidInput);
    if (!amount || amount < minBid) {
      showToast({
        kind: 'warn',
        title: 'مزايدة أقل من الحد الأدنى',
        body: `الحد الأدنى للمزايدة ${minBid.toLocaleString('en-US')} ر.س`,
      });
      return;
    }
    try {
      await socketRef.current.placeBid(amount);
      setBidInput('');
      showToast({ kind: 'success', title: '✓ تم تسجيل مزايدتك', duration: 2000 });
    } catch (e) {
      showToast({ kind: 'error', title: 'فشلت المزايدة', body: (e as Error).message });
    }
  };
  const handleToggleMic = () => {
    if (!socketRef.current) return;
    const next = !myMicOn;
    setMyMicOn(next);
    socketRef.current.toggleMic(next);
  };
  const handleRequestSpeak = () => {
    if (!socketRef.current || !user) return;
    socketRef.current.requestSpeak();
  };
  const handleGrantMic = (uid: string) => socketRef.current?.grantMic(uid);
  const handleDenyMic  = (uid: string) => socketRef.current?.denySpeakRequest(uid);
  const handleRevokeMic = (uid: string) => socketRef.current?.revokeMic(uid);

  const activeSpeakers = attendees.filter((a) => a.userId && grantedMics.has(a.userId));

  // 🔍 SEO — كل مزاد له صفحة محسّنة لمحركات البحث
  const auctionStatusAr = auction.status === 'LIVE' ? 'مباشر الآن' : auction.status === 'SCHEDULED' ? 'قادم' : auction.status === 'COMPLETED' || auction.status === 'ENDED' ? 'منتهي' : 'ملغي';
  const seoTitle = `مزاد ${auction.title} ${auction.city ? `في ${auction.city}` : ''} — ${auctionStatusAr} 🔨`;
  const seoDescription = (auction.description ?? `مزاد عقاري ${auctionStatusAr.toLowerCase()} على ${auction.title}${auction.city ? ` في ${auction.city}` : ''}. سعر الافتتاح ${Number(auction.openingPrice).toLocaleString('en-US')} ر.س. شارك في المزاد المباشر — شركة صورة العقارية، منصة المزادات العقارية المرخّصة من الهيئة العامة للعقار (REGA).`).slice(0, 280);
  const seoKeywords = `مزاد عقاري, مزاد ${auction.city ?? 'السعودية'}, مزادات حية, مزاد مباشر, ${auction.title}, مزاد ${auction.title}, مزاد على الهواء, مزاد إلكتروني, مزاد فلل, مزاد أراضي, شركة صورة العقارية, البورصة العقارية, REGA, img.sa`;
  const canonicalUrl = `https://img.sa/auctions/${auction.id}`;

  return (
    <div className="min-h-screen bg-[#0F1114] text-white">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={auction.coverImage ?? undefined}
        canonical={canonicalUrl}
        jsonLd={{
          '@type': 'Event',
          name: auction.title,
          description: auction.description ?? seoDescription,
          url: canonicalUrl,
          image: [auction.coverImage, ...(auction.images ?? [])].filter(Boolean),
          startDate: auction.startAt,
          endDate: auction.endAt,
          eventStatus: auction.status === 'CANCELLED'
            ? 'https://schema.org/EventCancelled'
            : auction.status === 'LIVE' || auction.status === 'SCHEDULED'
            ? 'https://schema.org/EventScheduled'
            : 'https://schema.org/EventCompleted',
          eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
          location: {
            '@type': 'VirtualLocation',
            url: canonicalUrl,
          },
          offers: {
            '@type': 'Offer',
            price: Number(auction.currentBid) || Number(auction.openingPrice),
            priceCurrency: 'SAR',
            availability: auction.status === 'LIVE' || auction.status === 'SCHEDULED'
              ? 'https://schema.org/InStock'
              : 'https://schema.org/SoldOut',
            url: canonicalUrl,
            validFrom: auction.startAt,
          },
          organizer: {
            '@type': 'Organization',
            name: 'شركة صورة العقارية',
            url: 'https://img.sa',
          },
        }}
      />
      {/* 🍞 Toast notifications (بديل alerts) */}
      <ToastContainer position="top-center" />

      {/* 🔔 Stage banner */}
      {stageBanner && (
        <div className="fixed top-0 left-0 right-0 z-50 py-3 text-center font-black text-lg shadow-2xl" style={{ background: stageBanner.color }}>
          {stageBanner.label}
        </div>
      )}

      {/* 🏆 Auction ended overlay */}
      {auctionEnded && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6">
          <div className={`bg-gradient-to-br rounded-3xl shadow-2xl p-10 max-w-md w-full text-center border-2 ${
            auctionEnded.winner
              ? 'from-navy-darker to-[#1F2937] border-gold/40'
              : auctionEnded.noSale
              ? 'from-[#3a1a1a] to-[#1F1010] border-amber-500/40'
              : 'from-navy-darker to-[#1F2937] border-gold/40'
          }`}>
            <div className="text-6xl mb-3">
              {auctionEnded.winner ? '🏆' : auctionEnded.noSale ? '⚠️' : '🔨'}
            </div>
            <h2 className="text-3xl font-black mb-2 text-white">
              {auctionEnded.winner
                ? 'تم البيع — الله يبارك للبائع والمشتري'
                : auctionEnded.noSale
                ? 'لم يتم البيع'
                : 'انتهى المزاد'}
            </h2>
            {auctionEnded.winner && (
              <p className="text-gold-light text-lg mb-3">الفائز: <strong className="text-white">{auctionEnded.winner.name}</strong></p>
            )}
            {/* 📝 سبب عدم البيع */}
            {auctionEnded.noSale && auctionEnded.noSaleReason && (
              <div className="bg-black/40 border border-amber-500/30 rounded-xl p-4 mb-5 text-start">
                <div className="text-[11px] text-amber-300 font-bold uppercase tracking-wider mb-1">السبب</div>
                <p className="text-sm text-white/85 leading-relaxed">{auctionEnded.noSaleReason}</p>
              </div>
            )}
            {!auctionEnded.noSale && (
              <div className="text-3xl font-black text-gold mb-6">
                {auctionEnded.finalAmount.toLocaleString('en-US')} <span className="text-base">ر.س</span>
              </div>
            )}
            <Link to="/auctions" className="inline-block px-8 py-3 bg-gold hover:bg-gold-dark rounded-xl font-black transition mt-4">
              العودة للمزادات
            </Link>
          </div>
        </div>
      )}

      {/* ❌ Auction cancelled overlay */}
      {auctionRes?.data?.status === 'CANCELLED' && !auctionEnded && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6">
          <div className="bg-gradient-to-br from-[#3a1a1a] to-[#1F1010] rounded-3xl shadow-2xl p-10 max-w-md w-full text-center border-2 border-red-500/40">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-4xl mb-4 shadow-lg">
              ⚠️
            </div>
            <h2 className="text-3xl font-black mb-2 text-white">تم إلغاء المزاد</h2>
            <p className="text-white/60 text-sm mb-4">
              تم إيقاف هذا المزاد ولن يقام في موعده الأصلي.
            </p>

            {/* السبب — يُعرض فقط إن كان مضافاً */}
            {auctionRes.data.cancellationReason && auctionRes.data.cancellationReason.trim() && (
              <div className="bg-black/40 border border-red-500/30 rounded-xl p-4 mb-5 text-start">
                <div className="text-[11px] text-red-300 font-bold uppercase tracking-wider mb-1">سبب الإلغاء</div>
                <p className="text-sm text-white/85 leading-relaxed">
                  {auctionRes.data.cancellationReason}
                </p>
              </div>
            )}

            {auctionRes.data.cancelledAt && (
              <div className="text-[11px] text-white/40 mb-5">
                تاريخ الإلغاء: {new Date(auctionRes.data.cancelledAt).toLocaleDateString('ar-SA')}
              </div>
            )}

            <Link
              to="/auctions"
              className="inline-block px-8 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-black transition border border-white/20 text-white"
            >
              العودة للمزادات
            </Link>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <Link to="/auctions" className="text-gold-light hover:text-gold transition text-sm font-bold">← العودة للمزادات</Link>
          {auction.status === 'LIVE' ? (
            <Badge color="red" className="!text-sm animate-pulse">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500 ml-1.5"></span> مباشر
            </Badge>
          ) : auction.status === 'SCHEDULED' ? (
            <Badge color="navy">قادم</Badge>
          ) : (
            <Badge color="gray">{auction.status}</Badge>
          )}
        </div>

        <h1 className="text-2xl md:text-3xl font-black mb-1">{auction.title}</h1>
        <p className="text-gold-light text-sm mb-5">📍 {auction.city ?? '—'}</p>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
          {/* MAIN PANEL */}
          <div className="space-y-4">
            {/* Image carousel */}
            <div className="aspect-video rounded-2xl overflow-hidden bg-gradient-to-br from-navy to-navy-darker relative shadow-xl">
              {images[imgIndex] ? (
                <img src={images[imgIndex]} alt={auction.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-7xl">🏠</div>
              )}
              {images.length > 1 && (
                <>
                  <button onClick={() => setImgIndex((i) => (i - 1 + images.length) % images.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-xl">‹</button>
                  <button onClick={() => setImgIndex((i) => (i + 1) % images.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-xl">›</button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 bg-black/50 px-3 py-1.5 rounded-full">
                    {images.map((_, i) => (
                      <button key={i} onClick={() => setImgIndex(i)} className={`w-2 h-2 rounded-full transition ${i === imgIndex ? 'bg-gold w-6' : 'bg-white/40'}`} />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* 📊 4 KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="سعر الافتتاح"   value={Number(auction.openingPrice).toLocaleString('en-US')} />
              <Stat label="المزايدة الحالية" value={currentBid.toLocaleString('en-US')} highlight />
              <Stat label="الحد التالي"      value={minBid.toLocaleString('en-US')} accent />
              <Stat label="إجمالي المزايدات" value={`${auction.totalBids ?? bids.length}`} />
            </div>

            {/* 🔨 Auctioneer controls — أعلى موقع لأهميتها للمحرّج */}
            {auction.status === 'LIVE' && canAuctioneer && !auctionEnded && (
              <Card className="!bg-gradient-to-br from-amber-900/60 to-red-900/60 !border-amber-400/40 !ring-2 !ring-gold/30 !shadow-2xl">
                <CardBody>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-3xl">🔨</span>
                    <div>
                      <div className="font-black text-amber-200">تحكّم المحرّج</div>
                      <div className="text-xs text-amber-100/70">المراحل المحدّدة: 1 → 2 → 3 → بيع (لا يمكن التجاوز)</div>
                    </div>
                  </div>

                  {/* Stage buttons — خطّي محدود */}
                  <div className="grid grid-cols-[2fr_1fr] gap-2">
                    <button onClick={handleStageNext} disabled={isCooling || stageDone} className="py-4 rounded-xl font-black text-lg transition disabled:opacity-60" style={{ background: stageBg, color: '#fff' }}>
                      {stageLabel}
                    </button>
                    <button onClick={handleStageRepeat} disabled={isCooling || stageProgress < 1 || stageProgress >= 4} className="py-4 rounded-xl font-black text-sm bg-white/15 hover:bg-white/25 transition disabled:opacity-50">
                      ⟳ تكرار
                    </button>
                  </div>

                  {/* مؤشّر التقدم */}
                  <div className="flex items-center gap-2 mt-3">
                    {[1, 2, 3, 4].map((s) => (
                      <div key={s} className={`flex-1 h-1.5 rounded-full transition ${stageProgress >= s ? 'bg-gold' : 'bg-white/15'}`}></div>
                    ))}
                  </div>

                  {/* ❌ زر "لم يتم البيع" — يفتح مودال السبب */}
                  <button
                    onClick={() => setShowNotSold(true)}
                    disabled={stageDone}
                    className="w-full mt-3 py-3 rounded-xl font-black text-sm border-2 border-red-500/30 text-red-200 hover:bg-red-500/10 transition disabled:opacity-50"
                  >
                    ❌ لم يتم البيع
                  </button>

                  {/* Mic toggle */}
                  {canVoice && (
                    <button onClick={handleToggleMic} className={`w-full mt-3 py-3 rounded-xl font-black text-base transition ${myMicOn ? 'bg-green-600 hover:bg-green-700' : 'bg-white/10 hover:bg-white/20'}`}>
                      {myMicOn ? '🔴 المايك مفتوح — اضغط للإغلاق' : '🎤 فتح المايك'}
                    </button>
                  )}
                </CardBody>
              </Card>
            )}

            {/* 💰 تفصيل السعر */}
            <Card className="!bg-gradient-to-br from-[#1A1F2E] to-[#0F1114] !border-gold/30">
              <CardBody>
                <h3 className="font-black text-gold-light text-sm mb-3">💎 تفصيل سعر الإغلاق</h3>
                <div className="space-y-2 text-sm">
                  <Row label="سعر المزايدة" value={`${currentBid.toLocaleString('en-US')} ر.س`} />
                  <Row label={`السعي (${(commissionRate * 100).toFixed(2)}%)`}     value={`${commission.toLocaleString('en-US', { maximumFractionDigits: 2 })} ر.س`} />
                  <Row label={`ضريبة التصرفات العقارية (5%)`}                       value={`${tax.toLocaleString('en-US', { maximumFractionDigits: 2 })} ر.س`} />
                  <div className="border-t border-white/10 pt-2 mt-2">
                    <Row label="الإجمالي المستحق" value={`${totalDue.toLocaleString('en-US', { maximumFractionDigits: 2 })} ر.س`} highlight />
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* ⏱ Countdown */}
            <Card className="!bg-gradient-to-br from-[#1A1F2E] to-[#0F1114] !border-gold/30">
              <CardBody>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-xs text-gray-400 mb-2">{auction.status === 'LIVE' ? 'ينتهي المزاد خلال' : 'يبدأ المزاد خلال'}</div>
                    <div className="flex gap-2" dir="ltr">
                      <TimeBox value={dd} label="يوم" />
                      <TimeBox value={hh} label="ساعة" />
                      <TimeBox value={mm} label="دقيقة" />
                      <TimeBox value={ss} label="ثانية" />
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* 💰 شريط المزايدة العائم — صار fixed أسفل الشاشة (انظر تحت) */}
            {/* المساحة هنا تركت فاضية ليُحجز ارتفاع الـ bid bar */}
            {auction.status === 'LIVE' && !auctionEnded && !canAuctioneer && (
              <div className="h-32" aria-hidden="true" />
            )}

            {/* 🎤 Active speakers (مكوّنات المايك المفتوح) */}
            {activeSpeakers.length > 0 && (
              <Card className="!bg-gradient-to-br from-green-900/40 to-green-700/20 !border-green-500/30">
                <CardBody>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🎤</span>
                    <div className="font-black text-green-300">يتحدّث الآن</div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {activeSpeakers.map((s, i) => (
                      <div key={`${s.userId}-${i}`} className="flex items-center gap-2 bg-black/30 rounded-lg px-3 py-1.5">
                        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                        <span className="text-sm font-bold">{s.name}</span>
                        {canGrantMic && s.userId !== user?.id && (
                          <button onClick={() => handleRevokeMic(s.userId!)} className="text-red-400 hover:text-red-300 text-xs">✕</button>
                        )}
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            )}

            {/* ✋ Pending speak requests (للمشرف) */}
            {canGrantMic && pendingRequests.length > 0 && (
              <Card className="!bg-gradient-to-br from-amber-900/40 to-amber-700/20 !border-amber-500/30">
                <CardBody>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">✋</span>
                    <div className="font-black text-amber-300">طلبات تحدّث ({pendingRequests.length})</div>
                  </div>
                  <div className="space-y-2">
                    {pendingRequests.map((r) => (
                      <div key={r.userId} className="flex items-center justify-between bg-black/30 rounded-lg p-2">
                        <span className="text-sm font-bold">{r.name}</span>
                        <div className="flex gap-2">
                          <button onClick={() => handleGrantMic(r.userId)}  className="px-3 py-1 bg-green-600 hover:bg-green-700 rounded-lg text-xs font-bold">✓ منح المايك</button>
                          <button onClick={() => handleDenyMic(r.userId)}   className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded-lg text-xs font-bold">✕ رفض</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            )}

            {/* ✋ Speak request button (لغير المحرّج) */}
            {auction.status === 'LIVE' && user && !canAuctioneer && !grantedMics.has(user.id) && (
              <Card>
                <CardBody className="!bg-white/5 text-center">
                  <button onClick={handleRequestSpeak} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-xl font-bold text-sm transition">
                    ✋ طلب تحدّث
                  </button>
                </CardBody>
              </Card>
            )}

            {/* 🎙️ User mic toggle (لو منحه المشرف المايك) */}
            {auction.status === 'LIVE' && user && !canAuctioneer && grantedMics.has(user.id) && (
              <Card>
                <CardBody className="!bg-green-900/30 text-center">
                  <button onClick={handleToggleMic} className={`px-6 py-2.5 rounded-xl font-bold text-sm transition ${myMicOn ? 'bg-green-600 hover:bg-green-700' : 'bg-white/10 hover:bg-white/20'}`}>
                    {myMicOn ? '🔴 المايك مفتوح — اضغط للإغلاق' : '🎤 المايك ممنوح — افتح'}
                  </button>
                </CardBody>
              </Card>
            )}
          </div>

          {/* SIDEBAR */}
          <aside className="bg-[#161B22] rounded-2xl border border-white/10 overflow-hidden flex flex-col h-[700px]">
            <div className="flex border-b border-white/10">
              <TabBtn active={tab === 'bids'}      onClick={() => setTab('bids')}>💰 المزايدات ({bids.length})</TabBtn>
              <TabBtn active={tab === 'attendees'} onClick={() => setTab('attendees')}>👥 الحضور ({attendees.length})</TabBtn>
              <TabBtn active={tab === 'chat'}      onClick={() => setTab('chat')}>💬 الشات</TabBtn>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              {tab === 'bids' && (
                <div className="space-y-2">
                  {bids.length === 0 ? <p className="text-gray-500 text-center text-sm py-8">لا توجد مزايدات بعد</p> :
                    bids.map((b) => (
                      <div key={b.id} className="flex items-center justify-between bg-white/5 rounded-lg p-2 text-sm">
                        <div>
                          <div className="font-bold">{b.name}</div>
                          <div className="text-xs text-gray-400">{b.at.toLocaleTimeString('ar-SA')}</div>
                        </div>
                        <div className="text-gold font-black">{b.amount.toLocaleString('en-US')}</div>
                      </div>
                    ))
                  }
                </div>
              )}
              {tab === 'attendees' && (
                <div className="space-y-1">
                  {attendees.length === 0 ? <p className="text-gray-500 text-center text-sm py-8">لا يوجد حضور</p> :
                    attendees.map((a, i) => {
                      const isMuted = a.userId ? mutedUsers.has(a.userId) : false;
                      const isBanned = a.userId ? bannedUsers.has(a.userId) : false;
                      const hasMic = a.userId ? grantedMics.has(a.userId) : false;
                      const canControl = canGrantMic && a.userId && a.userId !== user?.id && !a.isAuctioneer;
                      return (
                        <div key={`${a.userId}-${i}`} className="text-sm py-2 border-b border-white/5">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              {hasMic && <span className="text-green-400 shrink-0">🎤</span>}
                              {isMuted && <span className="text-amber-400 shrink-0" title="مكتوم">🔇</span>}
                              {isBanned && <span className="text-red-400 shrink-0" title="محظور">⛔</span>}
                              <span className="truncate">{a.name}</span>
                            </div>
                            <div className="flex gap-1 shrink-0">
                              {a.isAuctioneer && <Badge color="gold">محرّج</Badge>}
                              {a.isModerator && !a.isAuctioneer && <Badge color="navy">مشرف</Badge>}
                              {a.verificationTier === 'DIAMOND' && <Badge color="navy">💎</Badge>}
                            </div>
                          </div>
                          {/* أزرار تحكّم المحرّج/المشرف بكل عضو */}
                          {canControl && (
                            <div className="flex gap-1 mt-1.5 flex-wrap">
                              {!hasMic ? (
                                <ActionBtn onClick={() => handleGrantMic(a.userId!)} title="منح المايك" color="green">🎤</ActionBtn>
                              ) : (
                                <ActionBtn onClick={() => handleRevokeMic(a.userId!)} title="سحب المايك" color="amber">🔇</ActionBtn>
                              )}
                              {!isMuted ? (
                                <ActionBtn onClick={() => socketRef.current?.muteChat(a.userId!)} title="كتم من الشات" color="amber">💬✕</ActionBtn>
                              ) : (
                                <ActionBtn onClick={() => socketRef.current?.unmuteChat(a.userId!)} title="إلغاء الكتم" color="green">💬✓</ActionBtn>
                              )}
                              <ActionBtn
                                onClick={() => {
                                  if (confirm(`طرد ${a.name} من الغرفة؟`)) socketRef.current?.kickUser(a.userId!);
                                }}
                                title="طرد"
                                color="red"
                              >
                                🚫
                              </ActionBtn>
                              <ActionBtn
                                onClick={() => {
                                  if (confirm(`حظر ${a.name} نهائياً من هذا المزاد؟`)) socketRef.current?.banUser(a.userId!);
                                }}
                                title="حظر"
                                color="red"
                              >
                                ⛔
                              </ActionBtn>
                            </div>
                          )}
                        </div>
                      );
                    })
                  }
                </div>
              )}
              {tab === 'chat' && (
                <div className="space-y-2">
                  {chat.length === 0 ? <p className="text-gray-500 text-center text-sm py-8">ابدأ محادثة…</p> :
                    chat.map((m) => (
                      <div key={m.id} className={`text-sm ${m.userId === null ? 'text-gold-light italic text-center text-xs' : ''}`}>
                        {m.userId ? <strong className={m.isMine ? 'text-gold' : 'text-blue-300'}>{m.name}: </strong> : null}
                        {m.message}
                      </div>
                    ))
                  }
                </div>
              )}
            </div>

            {tab === 'chat' && (
              <div className="p-3 border-t border-white/10 flex gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
                  placeholder="اكتب رسالة…"
                  className="flex-1 px-3 py-2 rounded-lg bg-white/10 text-white placeholder:text-gray-500 outline-none focus:bg-white/15 text-sm"
                />
                <button onClick={handleSendChat} className="px-4 bg-gold hover:bg-gold-dark rounded-lg font-bold text-sm">
                  إرسال
                </button>
              </div>
            )}
          </aside>
        </div>
      </div>

      {/* 💰 شريط المزايدة العائم — fixed أسفل الشاشة (للمزايدين فقط، ليس للمحرّج) */}
      {auction.status === 'LIVE' && !auctionEnded && !canAuctioneer && (
        <FloatingBidBar
          minimized={bidBarMinimized}
          onToggle={() => setBidBarMinimized(!bidBarMinimized)}
          minBid={minBid}
          currentBid={currentBid}
          bidIncrement={Number(auction.bidIncrement)}
          bidInput={bidInput}
          setBidInput={setBidInput}
          onPlaceBid={handlePlaceBid}
          loggedIn={!!user}
        />
      )}

      {/* 💎 مودال تأكيد البيع (بديل confirm() المتصفح) */}
      {showSellConfirm && (
        <SellConfirmModal
          amount={currentBid}
          onConfirm={confirmSell}
          onCancel={() => setShowSellConfirm(false)}
        />
      )}

      {/* ❌ مودال "لم يتم البيع" مع السبب */}
      {showNotSold && (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" onClick={() => setShowNotSold(false)}>
          <div className="bg-gradient-to-br from-[#3a1a1a] to-[#1F1010] rounded-2xl shadow-2xl max-w-md w-full p-6 border-2 border-red-500/30" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-2">⚠️</div>
              <h3 className="text-xl font-black text-white mb-1">إغلاق المزاد بدون بيع</h3>
              <p className="text-sm text-white/70">سيُعلَن للجميع. اكتب سبب عدم البيع:</p>
            </div>
            <textarea
              value={notSoldReason}
              onChange={(e) => setNotSoldReason(e.target.value)}
              placeholder="مثلاً: عدم وصول السعر للحد المطلوب من البائع، أو سحب المالك للعقار…"
              className="w-full px-3 py-2.5 rounded-xl bg-black/40 border-2 border-red-500/30 text-white text-sm placeholder-white/40 focus:border-red-500 outline-none min-h-[100px]"
              autoFocus
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => { setShowNotSold(false); setNotSoldReason(''); }}
                className="flex-1 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white font-black text-sm transition"
              >
                تراجع
              </button>
              <button
                onClick={() => {
                  if (!socketRef.current) return;
                  if (notSoldReason.trim().length < 5) {
                    showToast({ kind: 'warn', title: 'اكتب سبباً واضحاً (٥ أحرف على الأقل)' });
                    return;
                  }
                  socketRef.current.notSold(notSoldReason.trim());
                  setShowNotSold(false);
                  setNotSoldReason('');
                }}
                className="flex-[2] py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-black text-sm transition"
              >
                ❌ تأكيد عدم البيع
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* 💰 شريط المزايدة العائم — يلتصق أسفل الشاشة دائماً */
function FloatingBidBar({
  minimized, onToggle, minBid, currentBid, bidIncrement,
  bidInput, setBidInput, onPlaceBid, loggedIn,
}: {
  minimized: boolean;
  onToggle: () => void;
  minBid: number;
  currentBid: number;
  bidIncrement: number;
  bidInput: string;
  setBidInput: (v: string) => void;
  onPlaceBid: () => void;
  loggedIn: boolean;
}) {
  // الوضع المصغّر: بطاقة صغيرة على اليسار
  if (minimized) {
    return (
      <button
        onClick={onToggle}
        className="fixed bottom-4 left-4 z-40 bg-gradient-to-br from-gold to-gold-dark text-white shadow-2xl rounded-2xl px-4 py-3 hover:scale-105 transition flex items-center gap-2 ring-2 ring-gold/40"
        title="فتح شريط المزايدة"
      >
        <span className="text-xl">🔨</span>
        <div className="text-start">
          <div className="text-[10px] opacity-80">الحد الأدنى</div>
          <div className="text-sm font-black tabular-nums leading-tight">
            {minBid.toLocaleString('en-US')} <span className="text-[10px]">ر.س</span>
          </div>
        </div>
        <span className="text-xs opacity-70">▲</span>
      </button>
    );
  }

  // الوضع الكامل: شريط ملتصق بأسفل الشاشة
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 px-3 pb-3 pointer-events-none">
      <div className="max-w-3xl mx-auto pointer-events-auto">
        <div className="bg-gradient-to-br from-[#1A1F2E]/95 to-[#0F1114]/95 backdrop-blur-xl border-2 border-gold/40 rounded-2xl shadow-2xl p-4 ring-1 ring-white/5">
          {/* رأس مع زرّ التصغير */}
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">💰</span>
              <div>
                <div className="font-black text-gold-light text-sm">المزايدة</div>
                <div className="text-[11px] text-gray-400">
                  الحد الأدنى: <span className="text-gold tabular-nums font-bold">{minBid.toLocaleString('en-US')}</span> ر.س
                </div>
              </div>
            </div>
            <button
              onClick={onToggle}
              className="text-white/40 hover:text-white text-sm w-7 h-7 rounded-lg hover:bg-white/10 flex items-center justify-center transition"
              title="تصغير"
              aria-label="تصغير شريط المزايدة"
            >
              ▼
            </button>
          </div>

          {/* المدخل + زر زايد */}
          <div className="flex gap-2">
            <input
              type="number"
              value={bidInput}
              onChange={(e) => setBidInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onPlaceBid()}
              placeholder={`${minBid}`}
              className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-600 outline-none focus:border-gold text-base"
            />
            <button
              onClick={onPlaceBid}
              className="px-6 py-3 bg-gradient-to-br from-gold to-gold-dark hover:opacity-90 rounded-xl font-black text-white transition shadow-lg shrink-0"
            >
              🔨 زايد
            </button>
          </div>

          {/* أزرار سريعة للإضافة */}
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {[1, 2, 5, 10].map((m) => {
              const v = m * bidIncrement;
              const lbl = v >= 1_000_000
                ? `${(v / 1_000_000).toFixed(1).replace(/\.0$/, '')}م`
                : `${Math.round(v / 1000)}ك`;
              return (
                <button
                  key={m}
                  onClick={() => setBidInput(String(currentBid + v))}
                  className="px-2.5 py-1 bg-gold/15 hover:bg-gold/25 border border-gold/30 rounded-lg text-[11px] font-bold text-gold-light transition"
                >
                  +{lbl} ر.س
                </button>
              );
            })}
            {!loggedIn && (
              <span className="ml-auto text-[11px] text-amber-300/70">🔐 سجّل دخولك للمزايدة</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* 💎 مودال تأكيد البيع — بديل أنيق لـ window.confirm */
function SellConfirmModal({
  amount, onConfirm, onCancel,
}: { amount: number; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[55] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-toast-in"
      onClick={onCancel}
    >
      <div
        className="bg-gradient-to-br from-[#1A1F2E] to-[#0F1114] border-2 border-gold/40 rounded-3xl shadow-2xl max-w-md w-full p-7 text-center text-white ring-2 ring-gold/20"
        onClick={(e) => e.stopPropagation()}
      >
        {/* أيقونة */}
        <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-4xl shadow-lg mb-4">
          🔨
        </div>

        <h3 className="text-2xl font-black mb-2">تأكيد البيع النهائي</h3>
        <p className="text-white/65 text-sm mb-5 leading-relaxed">
          هل أنت متأكد من إغلاق المزاد بسعر البيع التالي؟
        </p>

        {/* بطاقة السعر */}
        <div className="bg-black/40 rounded-2xl border border-gold/30 p-5 mb-6">
          <div className="text-[11px] text-gray-400 mb-1 uppercase tracking-wider">سعر البيع</div>
          <div className="text-4xl font-black text-gold tabular-nums leading-tight">
            {amount.toLocaleString('en-US')}
          </div>
          <div className="text-sm text-gold-light mt-1">ر.س</div>
        </div>

        {/* تنبيه */}
        <div className="bg-amber-900/30 border border-amber-500/30 rounded-xl p-3 mb-5 text-amber-200 text-[11px] leading-relaxed">
          ⚠️ بمجرد التأكيد سيُعلن عن البيع رسمياً ويُغلق المزاد، ولا يمكن التراجع عن العملية.
        </div>

        {/* الأزرار */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onCancel}
            className="py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold transition border border-white/10"
          >
            إلغاء
          </button>
          <button
            onClick={onConfirm}
            className="py-3 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 hover:from-emerald-400 hover:to-emerald-600 text-white font-black transition shadow-lg"
          >
            ✓ تأكيد البيع
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight, accent }: { label: string; value: string; highlight?: boolean; accent?: boolean }) {
  return (
    <div className={`rounded-xl p-3 border ${
      highlight ? 'bg-gradient-to-br from-blue-600 to-blue-700 border-blue-400' :
      accent ? 'bg-white/5 border-gold/30' :
      'bg-white/5 border-white/10'
    }`}>
      <div className={`text-xs ${highlight ? 'text-white/80' : 'text-gray-400'}`}>{label}</div>
      <div className={`text-lg font-black mt-1 ${accent ? 'text-gold-light' : ''}`}>{value}</div>
    </div>
  );
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-xs ${highlight ? 'text-gold-light font-bold' : 'text-gray-400'}`}>{label}</span>
      <span className={highlight ? 'text-gold font-black' : 'font-bold'}>{value}</span>
    </div>
  );
}

/* 🛡️ زر إجراء صغير على بطاقة الحاضر (منح/كتم/طرد/حظر) */
function ActionBtn({ onClick, title, color, children }: {
  onClick: () => void; title: string;
  color: 'green' | 'amber' | 'red';
  children: React.ReactNode;
}) {
  const cls =
    color === 'green' ? 'bg-emerald-600/20 hover:bg-emerald-600/35 text-emerald-300 border-emerald-500/30' :
    color === 'amber' ? 'bg-amber-600/20 hover:bg-amber-600/35 text-amber-300 border-amber-500/30' :
    /* red */           'bg-red-600/20 hover:bg-red-600/35 text-red-300 border-red-500/30';
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`px-2.5 py-1 rounded-lg border text-[11px] font-bold transition ${cls}`}
    >
      {children}
    </button>
  );
}

function TimeBox({ value, label }: { value: number; label: string }) {
  return (
    <div className="bg-black/50 border border-gold/30 rounded-lg px-3 py-2 text-center min-w-[54px]">
      <div className="font-black text-gold text-xl">{String(value).padStart(2, '0')}</div>
      <div className="text-xs text-gray-400">{label}</div>
    </div>
  );
}

function TabBtn({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`flex-1 py-3 text-sm font-bold transition border-b-2 ${
      active ? 'border-gold text-white bg-white/5' : 'border-transparent text-gray-400 hover:text-white hover:bg-white/5'
    }`}>
      {children}
    </button>
  );
}
